import { VerbRail } from '@components/VerbRail/VerbRail';
import { useLibraryVerbs } from '@lib/hooks/useLibraryVerbs';
import { pluralize } from '@lib/utils/units';
import { VERBS, verbPillItem, type LibraryVerbId } from '@lib/verbs';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Tooltip } from '@mui/material';
import {
  LibraryDestination,
  LibrarySwitch,
  emptyLibraryDisabledReason,
} from '@pages/Search/components/SearchBar/components/LibrarySwitch/LibrarySwitch';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { trackingStore } from '@stores/Tracking';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import css from './LibrariesVerbPills.module.scss';

/**
 * Shape of the two pills: whatever `verbPillItem` produces, plus what this surface adds.
 *
 * Derived rather than re-declared so a new catalog field can't leave this interface stale — and
 * so `outcome` keeps the non-optional type `verbPillItem` guarantees. A hand-written copy had
 * loosened it to `outcome?: string`, which would have let a labelled pill render with no
 * tooltip at all.
 */
type VerbPill = ReturnType<typeof verbPillItem<LibraryVerbId>> & {
  dispatch: (libraryId: string) => void;
  /** Present only on verbs whose destination can accept more than one library. */
  multiSelect?: {
    onConfirm: (libraryIds: string[]) => void;
    label: (count: number) => string;
  };
};

interface LibrariesVerbPillsProps {
  /**
   * Abbreviate to icon-only pills. Set by the nav row when it runs out of width — see
   * `VERBS_COLLAPSE_BELOW` in `LibrariesNav`.
   */
  collapsed?: boolean;
}

/**
 * The library verbs at the top of the Libraries page.
 *
 * The Library page can put these in its header unlabelled-by-scope, because that page *is*
 * one library. Here the page is every library, so each pill opens the app's existing library
 * picker to answer "which one?" — the same popover as the search bar, Add media and the
 * workspace signposts, via `LibrarySwitch`'s `trigger` prop. That reuse is the point: a
 * bespoke menu would be a fourth library list to keep in sync.
 *
 * Picking dispatches immediately; there is no confirm step and no selection that outlives
 * the gesture. Both verbs navigate away (the agent cross-origin, moments to /search), so
 * this component unmounts on use — which is why no pick is remembered and no row is
 * pre-checked. A check mark would claim a selection the user never made.
 *
 * One `LibrarySwitch` per pill, not one shared instance: `handleClick` anchors off the event
 * target so a single instance emitting both pills would anchor correctly, but it could not
 * tell which verb to run. One instance per verb knows by construction, and being the same
 * component they are identical by definition.
 */
export const LibrariesVerbPills = observer(function LibrariesVerbPills({
  collapsed,
}: LibrariesVerbPillsProps) {
  const libraryManager = useLibraryManager();
  const { workWithAgent, findMoments } = useLibraryVerbs();

  // The collapsed form gets its own `control` value, so labelled and icon-only conversion are
  // comparable. That's this surface's central bet — the labels are what make the verbs
  // discoverable — and it's only testable if the two forms don't report as one.
  const origin = {
    surface: 'libraries',
    control: collapsed ? 'libraries-header-pill-collapsed' : 'libraries-header-pill',
  } as const;

  // Unscoped outcome lines: this page is *every* library, so they say "a library". The Library
  // page's own action row uses the `scoped` variant ("this library").
  const verbs: VerbPill[] = [
    {
      ...verbPillItem('work-with-agent', { scoped: false }),
      dispatch: (libraryId: string) => workWithAgent(libraryId, origin),
      // No `multiSelect`: Agents takes one library at a time (`starzero-agents.ts`), so there is
      // nothing to gather — offering the switch would promise a scope the destination can't
      // accept.
    },
    {
      ...verbPillItem('find-moments', { scoped: false }),
      dispatch: (libraryId: string) => findMoments(libraryId, origin),
      // Search really does span libraries — one request per library, merged by score — so this
      // verb offers the same Multi-select switch the search bar has. Off, a click goes straight
      // to the results; on, the picker gathers a set and the footer button commits it.
      multiSelect: {
        onConfirm: (libraryIds: string[]) => findMoments(libraryIds, origin),
        // Names the action rather than the gesture, and states the scope being committed —
        // it's the last thing read before the click, and by then the header may have scrolled
        // out of view. Pluralized because "1 libraries" is the kind of detail that makes a
        // product feel unfinished.
        label: (count: number) =>
          count === 0
            ? VERBS['find-moments'].label
            : `${VERBS['find-moments'].label} in ${pluralize(count, 'library', 'libraries')}`,
      },
    },
  ];

  // Nothing to act on. The picker would open onto an empty list, which is a control that
  // can only fail.
  if (libraryManager.libraries.length === 0) return null;

  return (
    <div className={classNames(css.row, collapsed && css.collapsed)}>
      {verbs.map((verb) => (
        <LibrarySwitch
          key={verb.id}
          mode="destination"
          // These verbs act on media that already exists, so creating a library here would
          // hand back a target with nothing in it.
          allowNewLibrary={false}
          // No row pre-checked — see the component note. And with nothing ever checked, the
          // per-row checkbox would only ever render empty, implying a selection step that
          // doesn't exist here: picking a row runs the verb.
          destination={undefined}
          hideSelectionControl
          headerLabel={`${verb.label} in`}
          multiSelect={verb.multiSelect}
          onOpen={() =>
            trackingStore.libraryVerbPickerOpened({
              verb: verb.id,
              ...origin,
              libraryCount: libraryManager.libraries.length,
            })
          }
          // Both verbs read media, so a library with none is a dead end — the agent would
          // open a chat about nothing, the search would have nothing to match.
          disabledReasonFor={emptyLibraryDisabledReason}
          onDestinationChange={(next: LibraryDestination) => {
            if (next.kind === 'existing') verb.dispatch(next.library.libraryId);
          }}
          trigger={({ onClick }) =>
            collapsed ? (
              // Same component as the library header's collapsed pills, so the two surfaces
              // are identical by construction rather than by matching values: a frosted
              // bevel circle with the label in a tooltip. One item per rail because each
              // circle is its own picker trigger — VerbRail forwards the click event, which
              // is what lets the popover anchor to the button that opened it.
              <VerbRail
                variant="overlay"
                items={[
                  {
                    id: verb.id,
                    label: verb.label,
                    icon: verb.icon,
                    accent: verb.accent,
                    onClick,
                  },
                ]}
              />
            ) : (
              <Tooltip title={verb.outcome} placement="bottom">
                <button
                  type="button"
                  className={verb.accent ? css.cta : css.glass}
                  onClick={onClick}
                >
                  <span className={css.icon}>{verb.icon}</span>
                  {verb.label}
                  <ExpandMoreIcon className={css.chevron} />
                </button>
              </Tooltip>
            )
          }
        />
      ))}
    </div>
  );
});
